/**
 * @module engine/export/pdf/sheet
 * @summary The paper: sizes, plot scale, frame, grid references, title block,
 *   scale bar, north arrow and issue stamp.
 * @description
 *   A PFD can get away with a picture and a caption. A plant LAYOUT cannot: the
 *   supplier reading it needs to know how big things are, which way round the
 *   building faces, how to name the spot they are asking about, and whether the
 *   sheet in their hand is the one they should be quoting against. Everything in
 *   this module exists to answer one of those four questions.
 *
 *   - **Plot scale** — the layout is plotted at a STANDARD ratio (1:100, 1:200,
 *     …) chosen as the tightest one that still fits the sheet, not stretched to
 *     fill it. That is what lets a supplier put a rule on the paper, or measure
 *     in a PDF viewer, and get a real dimension back.
 *   - **Scale bar** — survives photocopying and re-scaled printing, which the
 *     printed ratio does not.
 *   - **Grid references** — so "the mixer at D-4" means something over the phone.
 *   - **Title block + stamp** — who drew it, when, at what revision, and whether
 *     it is issued for quotation or still preliminary.
 *
 *   Everything here is drawn straight through jsPDF in millimetres. Only the
 *   drawing itself goes through `PdfCanvas`.
 */
import type { jsPDF } from 'jspdf';

/** Sheet sizes in mm, portrait (width × height); landscape swaps them. */
export const PAPERS = {
  A4: { w: 210, h: 297 },
  A3: { w: 297, h: 420 },
  A2: { w: 420, h: 594 },
  A1: { w: 594, h: 841 },
  A0: { w: 841, h: 1189 },
} as const;

export type PaperName = keyof typeof PAPERS;

/**
 * The plot scales an engineering drawing is allowed to use. Arbitrary ratios
 * like 1:137 are useless to read off, so the exporter always lands on one of
 * these — bigger sheet or a coarser ratio, never a made-up number.
 */
export const SCALE_LADDER = [
  1, 2, 5, 10, 20, 25, 50, 100, 200, 250, 500, 1000, 2000, 2500, 5000, 10000,
] as const;

/** Millimetres per drawing unit → the tightest standard ratio that still fits. */
export function pickScale(realMm: number, availMm: number): number {
  if (!(realMm > 0) || !(availMm > 0)) return 100;
  const exact = realMm / availMm;
  for (const s of SCALE_LADDER) if (s >= exact) return s;
  return SCALE_LADDER[SCALE_LADDER.length - 1];
}

/** Everything the title block prints. */
export interface TitleInfo {
  docName: string;
  rev: string;
  date: string;
  drawnBy: string;
  approvedBy: string;
  /** e.g. `'ISSUED FOR QUOTATION'`. Empty prints nothing. */
  status: string;
  /** Sheet subtitle, e.g. `'PLANT LAYOUT'` or `'EQUIPMENT SCHEDULE'`. */
  sheetTitle: string;
  sheetNo: number;
  sheetCount: number;
  /** Printed in the SCALE cell — `'1:100'`, or `'NTS'` for a non-scaled sheet. */
  scaleText: string;
  paper: PaperName;
}

const INK = { r: 26, g: 26, b: 26 };
const BRAND = { r: 43, g: 87, b: 154 };
const FAINT = { r: 140, g: 140, b: 140 };

/** A rectangle in page millimetres. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Where everything on a sheet of a given size ends up. */
export interface SheetGeometry {
  pageW: number;
  pageH: number;
  /** The outer border. */
  frame: Rect;
  /** Inside the frame line, inside the grid-reference band. */
  inner: Rect;
  /** The title block, bottom-right of `inner`. */
  titleRect: Rect;
  /** What is left for the drawing once the title-block strip is reserved. */
  plot: Rect;
}

/**
 * Lay a sheet out from its size alone — no document needed. Kept pure so the
 * export dialog can tell the user which scale they are about to get BEFORE a
 * PDF exists to measure.
 */
export function sheetGeometry(paper: PaperName, orientation: 'landscape' | 'portrait'): SheetGeometry {
  const p = PAPERS[paper] || PAPERS.A1;
  const pageW = orientation === 'landscape' ? p.h : p.w;
  const pageH = orientation === 'landscape' ? p.w : p.h;

  // ISO 5457 margins: a wider left edge so a filed print can be bound without
  // the binding eating the drawing.
  const mL = 20;
  const m = 10;
  const frame: Rect = { x: mL, y: m, w: pageW - mL - m, h: pageH - m * 2 };
  // A band inside the frame carries the grid-reference letters and numbers.
  const band = 6;
  const inner: Rect = {
    x: frame.x + band,
    y: frame.y + band,
    w: frame.w - band * 2,
    h: frame.h - band * 2,
  };
  const tbH = Math.min(44, inner.h * 0.22);
  const tbW = Math.min(180, inner.w * 0.62);
  const titleRect: Rect = {
    x: inner.x + inner.w - tbW,
    y: inner.y + inner.h - tbH,
    w: tbW,
    h: tbH,
  };
  // Reserve the full-width strip the title block sits in, so the drawing can
  // never end up underneath it however the fit works out.
  const plot: Rect = {
    x: inner.x + 2,
    y: inner.y + 2,
    w: inner.w - 4,
    h: inner.h - tbH - 5,
  };
  return { pageW, pageH, frame, inner, titleRect, plot };
}

/**
 * One sheet of paper: knows its own frame, and hands back the rectangle the
 * drawing is allowed to occupy once the furniture has taken its share.
 */
export class Sheet {
  readonly doc: jsPDF;
  readonly pageW: number;
  readonly pageH: number;
  /** Inside the frame line, inside the grid-reference band. */
  readonly inner: Rect;
  /** The title block's rectangle, bottom-right of `inner`. */
  readonly titleRect: Rect;
  /** What is left for the drawing once the title-block strip is reserved. */
  readonly plot: Rect;
  /** The outer border, outside the grid-reference band. */
  private readonly frame: Rect;

  constructor(doc: jsPDF, paper: PaperName, orientation: 'landscape' | 'portrait') {
    this.doc = doc;
    const g = sheetGeometry(paper, orientation);
    this.pageW = g.pageW;
    this.pageH = g.pageH;
    this.frame = g.frame;
    this.inner = g.inner;
    this.titleRect = g.titleRect;
    this.plot = g.plot;
  }

  /** The border, and the grid-reference ticks and letters around it. */
  drawFrame(): void {
    const d = this.doc;
    const f = this.frame;
    d.setLineDashPattern([], 0);
    d.setDrawColor(INK.r, INK.g, INK.b);
    d.setLineWidth(0.7);
    d.rect(f.x, f.y, f.w, f.h);
    d.setLineWidth(0.25);
    d.rect(this.inner.x, this.inner.y, this.inner.w, this.inner.h);

    // Modules of at most 60 mm, so a reference never covers a vague area.
    const cols = Math.max(2, Math.ceil(this.inner.w / 60));
    const rows = Math.max(2, Math.ceil(this.inner.h / 60));
    const cw = this.inner.w / cols;
    const rh = this.inner.h / rows;
    d.setFont('helvetica', 'bold');
    d.setFontSize(7);
    d.setTextColor(INK.r, INK.g, INK.b);

    for (let i = 0; i < cols; i++) {
      const cx = this.inner.x + cw * (i + 0.5);
      const label = String(i + 1);
      d.text(label, cx, f.y + 4.4, { align: 'center' });
      d.text(label, cx, f.y + f.h - 1.6, { align: 'center' });
      if (i > 0) {
        const gx = this.inner.x + cw * i;
        d.line(gx, f.y, gx, this.inner.y);
        d.line(gx, this.inner.y + this.inner.h, gx, f.y + f.h);
      }
    }
    for (let j = 0; j < rows; j++) {
      const cy = this.inner.y + rh * (j + 0.5) + 1.2;
      const label = rowLetter(j);
      d.text(label, f.x + 3, cy, { align: 'center' });
      d.text(label, f.x + f.w - 3, cy, { align: 'center' });
      if (j > 0) {
        const gy = this.inner.y + rh * j;
        d.line(f.x, gy, this.inner.x, gy);
        d.line(this.inner.x + this.inner.w, gy, f.x + f.w, gy);
      }
    }
  }

  /** The title block. Drawn last so it sits over anything that strays into it. */
  drawTitleBlock(info: TitleInfo): void {
    const d = this.doc;
    const { x, y, w, h } = this.titleRect;
    d.setLineDashPattern([], 0);
    d.setFillColor(255, 255, 255);
    d.rect(x, y, w, h, 'F');
    d.setDrawColor(INK.r, INK.g, INK.b);
    d.setLineWidth(0.6);
    d.rect(x, y, w, h);

    const r1 = h * 0.32;
    const r2 = h * 0.23;
    const r3 = h * 0.23;
    const yA = y + r1;
    const yB = yA + r2;
    const yC = yB + r3;
    d.setLineWidth(0.25);
    [yA, yB, yC].forEach((ly) => d.line(x, ly, x + w, ly));

    // Row 1 — who, and what the drawing is.
    const split1 = x + w * 0.42;
    d.line(split1, y, split1, yA);
    d.setTextColor(BRAND.r, BRAND.g, BRAND.b);
    d.setFont('helvetica', 'bold');
    d.setFontSize(Math.min(9.5, r1 * 0.55));
    d.text('JEDDAH CABLES COMPANY', x + 2.5, y + r1 * 0.45);
    d.setFont('helvetica', 'normal');
    d.setFontSize(Math.min(6.2, r1 * 0.36));
    d.setTextColor(FAINT.r, FAINT.g, FAINT.b);
    d.text('Process Engineering', x + 2.5, y + r1 * 0.78);

    label(d, split1 + 2.5, y + r1 * 0.35, 'DRAWING TITLE');
    d.setFont('helvetica', 'bold');
    d.setFontSize(Math.min(9, r1 * 0.5));
    d.setTextColor(INK.r, INK.g, INK.b);
    d.text(fit(d, info.docName || 'Untitled', w * 0.55 - 5), split1 + 2.5, y + r1 * 0.82);

    // Rows 2-3 — the facts, four columns wide.
    const cx = [x, x + w * 0.28, x + w * 0.56, x + w * 0.78, x + w];
    for (let i = 1; i < 4; i++) d.line(cx[i], yA, cx[i], yC);
    cell(d, cx[0] + 2.5, yA, r2, 'DRAWN BY', info.drawnBy, cx[1] - cx[0] - 4);
    cell(d, cx[1] + 2.5, yA, r2, 'DATE', info.date, cx[2] - cx[1] - 4);
    cell(d, cx[2] + 2.5, yA, r2, 'SHEET', `${info.sheetNo} of ${info.sheetCount}`, cx[3] - cx[2] - 4);
    cell(d, cx[3] + 2.5, yA, r2, 'SIZE', info.paper, cx[4] - cx[3] - 4);
    cell(d, cx[0] + 2.5, yB, r3, 'APPROVED BY', info.approvedBy, cx[1] - cx[0] - 4);
    cell(d, cx[1] + 2.5, yB, r3, 'SHEET TITLE', info.sheetTitle, cx[3] - cx[1] - 4);
    cell(d, cx[3] + 2.5, yB, r3, 'SCALE', info.scaleText, cx[4] - cx[3] - 4);

    // Row 4 — status and revision, the two things read first.
    const r4 = y + h - yC;
    const revX = x + w * 0.78;
    d.line(revX, yC, revX, y + h);
    if (info.status) {
      d.setFillColor(BRAND.r, BRAND.g, BRAND.b);
      d.rect(x, yC, revX - x, r4, 'F');
      d.setTextColor(255, 255, 255);
      d.setFont('helvetica', 'bold');
      d.setFontSize(Math.min(8.5, r4 * 0.55));
      d.text(info.status.toUpperCase(), x + 2.5, yC + r4 * 0.68);
    } else {
      label(d, x + 2.5, yC + r4 * 0.38, 'STATUS');
    }
    label(d, revX + 2.5, yC + r4 * 0.38, 'REV');
    d.setFont('helvetica', 'bold');
    d.setFontSize(Math.min(10, r4 * 0.62));
    d.setTextColor(INK.r, INK.g, INK.b);
    d.text(info.rev || 'A', revX + 2.5, yC + r4 * 0.85);
  }

  /**
   * A graphic scale bar, in metres, alternating filled and open
   * divisions. Returns its own width so the caller can lay out beside it.
   */
  drawScaleBar(x: number, y: number, ratio: number, maxW = 60): number {
    const d = this.doc;
    // Choose a round real-world length whose plotted width lands near maxW.
    const targetM = (maxW * ratio) / 1000;
    const stepM = roundStep(targetM / 4);
    const divs = 4;
    const perDiv = (stepM * 1000) / ratio; // mm on paper
    const barW = perDiv * divs;
    const barH = 2.2;

    d.setLineDashPattern([], 0);
    d.setLineWidth(0.25);
    d.setDrawColor(INK.r, INK.g, INK.b);
    for (let i = 0; i < divs; i++) {
      const bx = x + perDiv * i;
      d.setFillColor(i % 2 ? 255 : INK.r, i % 2 ? 255 : INK.g, i % 2 ? 255 : INK.b);
      d.rect(bx, y, perDiv, barH, 'FD');
    }
    d.setFont('helvetica', 'normal');
    d.setFontSize(6);
    d.setTextColor(INK.r, INK.g, INK.b);
    for (let i = 0; i <= divs; i++) {
      d.text(fmtM(stepM * i), x + perDiv * i, y + barH + 3, { align: 'center' });
    }
    d.setFont('helvetica', 'bold');
    d.setFontSize(6.5);
    d.text('metres', x + barW + 2, y + barH + 3);
    d.setFontSize(7.5);
    d.text(`SCALE 1:${ratio}`, x, y - 1.6);
    return barW + 14;
  }

  /**
   * Lay the scale bar and north arrow into the strip beside the title block,
   * and report how far right they reached.
   *
   * One method rather than two loose calls so the collision that matters — the
   * furniture walking into the title block on a small sheet — is a single number
   * the self-check can assert on.
   */
  drawFurniture(ratio: number, northDeg: number, nts: boolean): number {
    const y = this.titleRect.y + 6;
    let x = this.inner.x + 4;
    // A fifth of the sheet at most, and never wider than 70 mm: past that the
    // bar stops being read as a bar and starts being read as part of the drawing.
    if (!nts) x += this.drawScaleBar(x, y, ratio, Math.min(70, this.inner.w * 0.2));
    const r = 5.5;
    this.drawNorthArrow(x + r + 4, y + r * 0.8, r, northDeg);
    return x + r * 2 + 8;
  }

  /** A north arrow. `deg` is the bearing of north, clockwise from up. */
  drawNorthArrow(cx: number, cy: number, r: number, deg = 0): void {
    const d = this.doc;
    const rad = (deg * Math.PI) / 180;
    // Point up the page, then swing by the bearing.
    const p = (ang: number, rr: number): [number, number] => [
      cx + Math.sin(rad + ang) * rr,
      cy - Math.cos(rad + ang) * rr,
    ];
    const tip = p(0, r);
    const bl = p(Math.PI * 0.78, r * 0.62);
    const br = p(-Math.PI * 0.78, r * 0.62);
    const tail = p(Math.PI, r * 0.16);

    d.setLineDashPattern([], 0);
    d.setDrawColor(INK.r, INK.g, INK.b);
    d.setLineWidth(0.25);
    d.setFillColor(255, 255, 255);
    d.circle(cx, cy, r * 1.28, 'S');
    // Solid half / open half, so the arrow reads at a glance which way is north.
    d.setFillColor(INK.r, INK.g, INK.b);
    d.path([
      { op: 'm', c: tip },
      { op: 'l', c: br },
      { op: 'l', c: tail },
      { op: 'h' },
    ] as unknown[]);
    d.fill();
    d.setFillColor(255, 255, 255);
    d.path([
      { op: 'm', c: tip },
      { op: 'l', c: bl },
      { op: 'l', c: tail },
      { op: 'h' },
    ] as unknown[]);
    d.fillStroke();
    d.setFont('helvetica', 'bold');
    d.setFontSize(6.5);
    d.setTextColor(INK.r, INK.g, INK.b);
    d.text('N', cx, cy - r * 1.28 - 1.2, { align: 'center' });
  }

  /**
   * A big diagonal watermark for a sheet that must not be built from —
   * PRELIMINARY, or a superseded revision. Drawn faint and behind nothing,
   * because a stamp that hides the drawing defeats sending the drawing.
   */
  drawWatermark(text: string): void {
    if (!text) return;
    const d = this.doc;
    d.saveGraphicsState();
    const Ctor = (d as unknown as { GState: new (p: object) => object }).GState;
    d.setGState(new Ctor({ opacity: 0.1 }) as never);
    d.setFont('helvetica', 'bold');
    d.setFontSize(Math.min(this.pageW * 0.17, 120));
    d.setTextColor(BRAND.r, BRAND.g, BRAND.b);
    d.text(text.toUpperCase(), this.pageW / 2, this.pageH / 2, {
      align: 'center',
      baseline: 'middle',
      angle: -Math.atan2(this.pageH, this.pageW) * (180 / Math.PI),
      rotationDirection: 0,
    });
    d.restoreGraphicsState();
  }
}

// ─── Small drawing helpers ───

/** A field label: tiny, grey, upper case. */
function label(d: jsPDF, x: number, y: number, txt: string): void {
  d.setFont('helvetica', 'normal');
  d.setFontSize(5.2);
  d.setTextColor(FAINT.r, FAINT.g, FAINT.b);
  d.text(txt, x, y);
}

/** A label + its value, stacked inside a row of height `h` starting at `y`. */
function cell(d: jsPDF, x: number, y: number, h: number, lbl: string, value: string, maxW: number): void {
  label(d, x, y + h * 0.4, lbl);
  d.setFont('helvetica', 'bold');
  d.setFontSize(Math.min(7.6, h * 0.42));
  d.setTextColor(INK.r, INK.g, INK.b);
  d.text(fit(d, value || '—', maxW), x, y + h * 0.86);
}

/** Truncate to fit `maxW` mm at the font currently set, with an ellipsis. */
function fit(d: jsPDF, txt: string, maxW: number): string {
  const s = String(txt ?? '');
  if (d.getTextWidth(s) <= maxW) return s;
  let lo = 0;
  let hi = s.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (d.getTextWidth(s.slice(0, mid) + '…') <= maxW) lo = mid;
    else hi = mid - 1;
  }
  return s.slice(0, lo) + '…';
}

/** A-Z, then AA, AB … for a sheet tall enough to need it. */
function rowLetter(i: number): string {
  return i < 26 ? String.fromCharCode(65 + i) : rowLetter(Math.floor(i / 26) - 1) + rowLetter(i % 26);
}

/**
 * The largest 1/2/5×10ⁿ that is not bigger than `v` — the only division a scale
 * bar should ever be labelled in.
 *
 * It rounds DOWN rather than to nearest on purpose. Rounding to nearest can
 * return up to 1.33× the asked-for step, which makes the bar overrun the width
 * it was given and, on a small sheet, walk into the title block. Rounding down
 * costs a slightly shorter bar and guarantees it fits.
 */
function roundStep(v: number): number {
  if (!(v > 0)) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const n = v / mag;
  return (n < 2 ? 1 : n < 5 ? 2 : 5) * mag;
}

function fmtM(v: number): string {
  if (v === 0) return '0';
  return v < 1 ? String(Number(v.toFixed(2))) : String(Number(v.toFixed(1)));
}
